import { useState } from 'react';
import {
  Button,
  Caption1,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { TeamList } from '../features/teams/TeamList';
import { TeamDetailView } from '../features/teams/TeamDetailView';
import type { TeamSummaryDto } from '../api/types';
import '../features/groups/groups.css';
import '../features/teams/teams.css';

interface ResultRow {
  team: TeamSummaryDto;
  status: 'Executed' | 'PendingApproval' | 'Error';
  message?: string;
}

export function TeamsDeletePage() {
  const api = useApi();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'teams.delete')?.ticketMode ?? 'None';

  const [selected, setSelected] = useState<TeamSummaryDto[]>([]);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | undefined>();
  const [ticket, setTicket] = useState('');
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);

  const selectedIds = new Set(selected.map((t) => t.id));

  function toggle(t: TeamSummaryDto) {
    setSelected((prev) => (prev.some((x) => x.id === t.id) ? prev.filter((x) => x.id !== t.id) : [...prev, t]));
  }

  function reset() {
    setSelected([]);
    setReason('');
    setTicket('');
    setResults(null);
    setReasonError(undefined);
    setTicketError(undefined);
  }

  async function submit() {
    if (selected.length === 0) return;
    if (!reason.trim()) {
      setReasonError('Ein Grund ist erforderlich.');
      return;
    }
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setReasonError(undefined);
    setTicketError(undefined);
    setBusy(true);

    const rows: ResultRow[] = [];
    for (const t of selected) {
      try {
        const res = await api.deleteTeam(t.id, { reason: reason.trim(), ticketNumber: ticket.trim() || undefined });
        rows.push({
          team: t,
          status: res.status === 'PendingApproval' ? 'PendingApproval' : 'Executed',
          message: res.message ?? undefined,
        });
      } catch (e) {
        rows.push({ team: t, status: 'Error', message: e instanceof Error ? e.message : String(e) });
      }
    }
    setResults(rows);
    setBusy(false);
  }

  if (results) {
    return (
      <div>
        <PageHeader title="Teams löschen" />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
          {results.map((r) => (
            <MessageBar
              key={r.team.id}
              intent={r.status === 'Executed' ? 'success' : r.status === 'PendingApproval' ? 'info' : 'error'}
            >
              <MessageBarBody>
                <MessageBarTitle>{r.team.displayName}</MessageBarTitle>
                {r.status === 'PendingApproval'
                  ? 'Freigabepflichtig — Antrag eingereicht (siehe „Meine Anfragen").'
                  : r.status === 'Executed'
                    ? (r.message ?? 'Gelöscht.')
                    : r.message}
              </MessageBarBody>
            </MessageBar>
          ))}
          <div>
            <Button appearance="primary" onClick={reset}>
              Weitere Löschung
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Teams löschen"
        description="Teams links wählen, Grund angeben und löschen. Standardmäßig freigabepflichtig (4-Augen)."
      />
      <div className="grp-layout">
        <TeamList selected={selectedIds} onToggle={toggle} />

        <div className="grp-detail-stack">
          {selected.length === 0 ? (
            <div className="grp-panel">
              <div className="grp-empty">Wähle links ein oder mehrere Teams zum Löschen.</div>
            </div>
          ) : (
            <>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                {selected.length} {selected.length === 1 ? 'Team' : 'Teams'} ausgewählt
              </Caption1>
              {selected.map((t) => (
                <TeamDetailView key={t.id} team={t} />
              ))}

              <MessageBar intent="warning">
                <MessageBarBody>
                  <MessageBarTitle>Achtung</MessageBarTitle>
                  Das Löschen entfernt das Team samt M365-Gruppe, Postfach und SharePoint-Site. Wiederherstellbar für 30
                  Tage.
                </MessageBarBody>
              </MessageBar>

              <Field
                label="Grund"
                required
                validationMessage={reasonError}
                hint="Pflicht — wird im Audit-Log und ggf. im Freigabe-Antrag protokolliert."
              >
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  rows={3}
                  placeholder="Warum wird das Team gelöscht?"
                />
              </Field>

              {ticketMode !== 'None' ? (
                <Field
                  label="Ticketnummer"
                  required={ticketMode === 'Required'}
                  validationMessage={ticketError}
                  hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
                >
                  <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. INC0012345" />
                </Field>
              ) : null}

              <div>
                <Button
                  appearance="primary"
                  style={{ backgroundColor: tokens.colorPaletteRedBackground3 }}
                  disabled={busy || !reason.trim()}
                  onClick={submit}
                >
                  {busy ? 'Bitte warten…' : selected.length > 1 ? `${selected.length} Teams löschen` : 'Team löschen'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
